/* reformly-cc.js
 *
 * Creator Contest page. Two jobs: post the entry form through the App Proxy,
 * and drive the terms sheet.
 *
 * The proxy is the same one the onboarding quiz uses (/apps/reformly-api) — a
 * same-origin path, so the API host never appears in client code and Shopify
 * signs every request. The path is a section setting, read off the form.
 */
(function () {
  'use strict';

  /* Three sections load this file (faq, entry, footer) so none of them depends
     on another being present. Identical src is fetched once but executed once
     per tag, which would bind every listener two or three times. */
  if (window.__rccInit) return;
  window.__rccInit = true;

  /* ---------------------------------------------------------------------
     PostHog

     Same shape as reformly-005.js: a registered pageset so this page is
     separable in reports, and one prefix on every event name. PostHog itself is
     initialised in theme.liquid — this only adds the page's own events.
     --------------------------------------------------------------------- */
  var PH_PREFIX = 'rcc_';
  var PH_TEMPLATE = 'reformly-creator-contest';
  if (window.posthog && window.posthog.register) {
    window.posthog.register({ reformly_pageset: 'creator-contest' });
  }
  function ph(event, props) {
    if (!window.posthog) return;
    var p = props || {};
    p.page_template = PH_TEMPLATE;
    window.posthog.capture(PH_PREFIX + event, p);
  }

  ph('page_view', { url: window.location.href, referrer: document.referrer });

  /* Scroll depth — each threshold once per pageview */
  (function () {
    var marks = [25, 50, 75, 100];
    var hit = {};
    var ticking = false;
    function check() {
      ticking = false;
      var doc = document.documentElement;
      var scrollable = doc.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return;
      var pct = ((window.pageYOffset || doc.scrollTop) / scrollable) * 100;
      marks.forEach(function (m) {
        if (pct >= m && !hit[m]) { hit[m] = true; ph('scroll_depth', { depth: m }); }
      });
    }
    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(check);
    }, { passive: true });
    check();
  })();

  /* Section reached — Shopify ids each section from the template key, so the
     name is hero / prizes / brief / gallery / steps / entry / notes / faq /
     final / footer without hardcoding a list here. */
  (function () {
    if (!window.IntersectionObserver) return;
    var sections = [].slice.call(document.querySelectorAll('[id^="shopify-section-"]'))
      .filter(function (el) { return el.querySelector('.rcc'); });
    if (!sections.length) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        io.unobserve(entry.target);
        ph('section_view', { section: entry.target.id.replace('shopify-section-', '') });
      });
    }, { threshold: 0.35 });
    sections.forEach(function (s) { io.observe(s); });
  })();

  /* ---------------------------------------------------------------------
     FAQ accordion — the 005 behaviour, one open at a time

     Native <details> snaps open in a single frame and shifts the page below it.
     Animating height needs a real measurement, so the panel is a plain div and
     the open state is a class rather than the [open] attribute.
     --------------------------------------------------------------------- */
  (function () {
    var items = [].slice.call(document.querySelectorAll('[data-rcc-faq]'));
    if (!items.length) return;

    // Collapse only now: without JS the answers stay visible rather than hidden
    [].forEach.call(document.querySelectorAll('[data-rcc-faq-list]'), function (l) {
      l.classList.add('is-ready');
    });

    function closeItem(it) {
      if (!it.classList.contains('is-open')) return;
      var pn = it.querySelector('.rcc-faq__ans');
      if (pn) {
        // Height is 'auto' while open, and auto does not transition — pin it to
        // the measured value first, then collapse on the next frame.
        pn.style.height = pn.scrollHeight + 'px';
        requestAnimationFrame(function () { pn.style.height = '0'; });
      }
      it.classList.remove('is-open');
      var b = it.querySelector('.rcc-faq__q');
      if (b) b.setAttribute('aria-expanded', 'false');
    }
    function openItem(it) {
      var pn = it.querySelector('.rcc-faq__ans');
      it.classList.add('is-open');
      if (pn) {
        // One measurement, write on the next frame — two forced layouts on the
        // tap itself is what makes this feel laggy.
        var target = pn.scrollHeight;
        pn.style.height = '0px';
        requestAnimationFrame(function () { pn.style.height = target + 'px'; });
      }
      var b = it.querySelector('.rcc-faq__q');
      if (b) b.setAttribute('aria-expanded', 'true');
    }

    items.forEach(function (it) {
      var pn = it.querySelector('.rcc-faq__ans');
      var btn = it.querySelector('.rcc-faq__q');
      if (!pn || !btn) return;
      // Back to auto once open, so a reflow (rotation, font swap) still fits
      pn.addEventListener('transitionend', function () {
        if (it.classList.contains('is-open')) pn.style.height = 'auto';
      });
      btn.addEventListener('click', function () {
        var willOpen = !it.classList.contains('is-open');
        items.forEach(closeItem);
        if (willOpen) {
          openItem(it);
          var q = btn.querySelector('span');
          ph('faq_open', { question: (q ? q.textContent : '').trim().slice(0, 120) });
        }
      });
    });
  })();

  /* ---------------------------------------------------------------------
     Terms sheet — opened from the form, the footer, anywhere
     --------------------------------------------------------------------- */
  var sheet = document.querySelector('[data-rcc-terms]');
  function openTerms() {
    if (!sheet) return;
    sheet.classList.add('is-open');
    document.documentElement.style.overflow = 'hidden';
  }
  function closeTerms() {
    if (!sheet) return;
    sheet.classList.remove('is-open');
    document.documentElement.style.overflow = '';
  }
  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-rcc-terms-open]')) {
      e.preventDefault();
      openTerms();
      ph('terms_open', { from: e.target.closest('.rcc-notes__card') ? 'rules_card' : 'link' });
      return;
    }
    if (e.target.closest('[data-rcc-terms-close]')) { closeTerms(); return; }
    // Scrim click closes; a click inside the sheet must not
    if (sheet && e.target === sheet) closeTerms();
  });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && sheet && sheet.classList.contains('is-open')) closeTerms();
  });

  /* Any CTA that jumps to the entry form */
  document.addEventListener('click', function (e) {
    var cta = e.target.closest('.rcc a[href^="#"], .rcc a[href*="#enter"]');
    if (!cta) return;
    ph('cta_click', {
      label: (cta.textContent || '').trim().slice(0, 60),
      target: cta.getAttribute('href')
    });
  });

  /* ---------------------------------------------------------------------
     Brief download

     The `download` attribute is ignored cross-origin, and the PDF lives on
     cdn.shopify.com while the page is on the shop domain — so every "download"
     link just opened a tab. Shopify serves Files with
     `access-control-allow-origin: *`, so fetching it and handing the browser a
     blob gives a real download with the filename we asked for.

     Anything that fails (offline, CORS changed, old browser) falls through to
     the link's normal behaviour, which is the tab it opened before.
     --------------------------------------------------------------------- */
  /* iOS is excluded on purpose.
     Two things break there and neither is recoverable: Safari on iOS ignores the
     download attribute for a cross-origin file, and the anchor we synthesise is
     clicked after `fetch` resolves — by then the user gesture has expired, so the
     click and the window.open fallback are both blocked silently. The result is a
     button that does nothing. Left alone, the link navigates to the PDF and iOS
     opens it in its viewer, which is what a phone should do anyway. */
  var isIOS = /iP(hone|od|ad)/.test(navigator.platform) ||
    (navigator.maxTouchPoints > 1 && /Mac/.test(navigator.platform));   // iPadOS reports MacIntel

  var downloading = false;
  document.addEventListener('click', function (e) {
    var link = e.target.closest('.rcc a[download]');
    if (!link || downloading || isIOS) return;

    var href = link.getAttribute('href');
    if (!href || href.charAt(0) === '#') return;
    // Same-origin already downloads natively — leave it alone.
    if (href.indexOf('//') !== -1 && href.indexOf(window.location.host) === -1) {
      e.preventDefault();
    } else {
      return;
    }

    var name = link.getAttribute('download') || 'download.pdf';
    var label = link.textContent;
    downloading = true;
    link.setAttribute('aria-busy', 'true');

    fetch(href, { mode: 'cors', credentials: 'omit' })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.blob();
      })
      .then(function (blob) {
        var url = URL.createObjectURL(blob);
        var a = document.createElement('a');
        a.href = url;
        a.download = name;
        document.body.appendChild(a);
        a.click();
        a.remove();
        // Revoke on the next tick: Safari needs the URL alive past the click.
        setTimeout(function () { URL.revokeObjectURL(url); }, 1000);
        ph('brief_download');
      })
      .catch(function () {
        window.open(href, '_blank', 'noopener');   // fall back to the old behaviour
      })
      .then(function () {
        downloading = false;
        link.removeAttribute('aria-busy');
        link.textContent = label;
      });
  });

  /* ---------------------------------------------------------------------
     Entry form
     --------------------------------------------------------------------- */
  var form = document.querySelector('[data-rcc-form]');
  if (!form) return;
  var wrap = form.closest('.rcc-entry');
  var btn = form.querySelector('[data-rcc-submit]');
  var errEl = form.querySelector('[data-rcc-error]');
  var busy = false;

  function fail(msg) {
    if (errEl) { errEl.textContent = msg; errEl.hidden = false; }
  }

  /* Native validation is the gate, but the bubble alone is easy to miss — it can
     render off-screen on a phone, and it points at a checkbox we have styled
     ourselves. So mark what is missing, say so in the error line, and take the
     visitor to the first offending control. */
  function markInvalid() {
    var first = null;
    var fields = form.querySelectorAll('input, select, textarea');
    Array.prototype.forEach.call(fields, function (el) {
      var row = el.closest('.rcc-entry__label, .rcc-entry__check') || el;
      var ok = el.checkValidity();
      row.classList.toggle('is-invalid', !ok);
      el.setAttribute('aria-invalid', ok ? 'false' : 'true');
      if (!ok && !first) first = el;
    });
    return first;
  }

  /* One entry_started per pageview, then one field_filled per field the first
     time it holds a valid value — that is what makes a drop-off funnel possible. */
  var started = false;
  var filled = {};
  function noteProgress(el) {
    if (!el.name && el.type !== 'checkbox') return;
    if (!started) { started = true; ph('entry_started'); }
    var key = el.name || (el.closest('.rcc-entry__check') ? 'confirm' : '');
    if (!key || filled[key]) return;
    var done = el.type === 'checkbox' ? el.checked : (el.value || '').trim() !== '';
    if (done && el.checkValidity()) {
      filled[key] = true;
      ph('entry_field_filled', { field: key, filled_count: Object.keys(filled).length });
    }
  }

  form.addEventListener('input', function (e) {
    noteProgress(e.target);
    var row = e.target.closest('.rcc-entry__label, .rcc-entry__check');
    if (row && e.target.checkValidity()) {
      row.classList.remove('is-invalid');
      e.target.setAttribute('aria-invalid', 'false');
    }
  });
  form.addEventListener('change', function (e) {
    noteProgress(e.target);
    // Checkboxes and the country <select> both report through change, not input
    var row = e.target.closest('.rcc-entry__check, .rcc-entry__label');
    if (row && e.target.checkValidity()) {
      row.classList.remove('is-invalid');
      e.target.setAttribute('aria-invalid', 'false');
    }
  });

  form.addEventListener('submit', function (e) {
    e.preventDefault();
    if (busy) return;

    var firstInvalid = markInvalid();
    if (firstInvalid) {
      var missingTick = form.querySelector('.rcc-entry__check.is-invalid');
      fail(missingTick && firstInvalid.type === 'checkbox'
        ? 'Please confirm the three statements above before submitting.'
        : 'Please fill in the highlighted fields before submitting.');
      ph('entry_invalid', { field: firstInvalid.name || firstInvalid.type });
      firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
      form.reportValidity();          // native bubble too, where it lands well
      return;
    }
    if (errEl) errEl.hidden = true;

    var base = (form.getAttribute('data-api-proxy') || '/apps/reformly-api').replace(/\/$/, '');
    var path = form.getAttribute('data-api-path') || '/creator-contest/entry';
    var data = new FormData(form);
    var payload = {
      first_name: (data.get('first') || '').trim(),
      last_name: (data.get('last') || '').trim(),
      email: (data.get('email') || '').trim(),
      country: (data.get('country') || '').trim(),
      social: (data.get('social') || '').trim(),
      footage_url: (data.get('link') || '').trim(),
      // Provenance, so entries can be told apart if the form is ever reused
      source: form.getAttribute('data-source') || 'creator-contest',
      page: window.location.pathname
    };

    busy = true;
    if (errEl) errEl.hidden = true;
    var label = btn ? btn.textContent : '';
    if (btn) { btn.disabled = true; btn.textContent = btn.getAttribute('data-busy-label') || 'Submitting…'; }

    fetch(base + path, {
      method: 'POST',
      /* Accept: application/json is not decoration — the App Proxy forwards the
         visitor's headers, so a tunnel or CDN in front of the API can otherwise
         answer with an HTML interstitial at status 200. */
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(function (res) {
        return res.text().then(function (text) {
          var body = null;
          try { body = text ? JSON.parse(text) : null; } catch (err) { body = null; }
          if (!res.ok) throw new Error((body && (body.message || body.error)) || 'Request failed (' + res.status + ')');
          return body;
        });
      })
      .then(function () {
        if (wrap) wrap.classList.add('is-submitted');
        if (wrap) wrap.scrollIntoView({ behavior: 'smooth', block: 'start' });
        ph('entry_submitted');
      })
      .catch(function (err) {
        ph('entry_failed', { message: String(err && err.message || err).slice(0, 120) });
        fail(form.getAttribute('data-error-message') ||
          'Something went wrong sending your entry. Please try again, or email us if it keeps failing.');
        if (window.console) console.error('[creator-contest]', err);
      })
      .then(function () {
        busy = false;
        if (btn) { btn.disabled = false; btn.textContent = label; }
      });
  });
})();
